import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import TimezoneSelect from 'react-timezone-select';
import { FLIGHT_FIELDS, validateFlightData, inferMissingFields, normalizeFlightData, useFlightFields } from '../flightUtils';
import { flightDataService } from '../../services/flightDataService';
import 'react-datepicker/dist/react-datepicker.css';
import './AddFlightForm.css';
import { EnhancedSelect } from '@trip-planner-monorepo/shared';
import { useParams } from 'react-router-dom';
import { useAuth } from '@trip-planner-monorepo/shared';

const AddFlightForm = ({ onSubmit, initialData = {}, onCancel }) => {
    const { tripId } = useParams();
    const { currentUser } = useAuth();
    const flightFields = useFlightFields(tripId);

    // Define the list of standard cities in one place for reuse
    const STANDARD_CITIES = [
        { value: 'NYC', label: 'New York', name: 'New York' },
        { value: 'LON', label: 'London', name: 'London' },
        { value: 'TOK', label: 'Tokyo', name: 'Tokyo' },
        { value: 'LAX', label: 'Los Angeles', name: 'Los Angeles' },
        { value: 'PAR', label: 'Paris', name: 'Paris' },
        { value: 'SYD', label: 'Sydney', name: 'Sydney' },
        { value: 'HKG', label: 'Hong Kong', name: 'Hong Kong' },
        { value: 'SIN', label: 'Singapore', name: 'Singapore' },
        { value: 'DXB', label: 'Dubai', name: 'Dubai' },
        { value: 'FRA', label: 'Frankfurt', name: 'Frankfurt' },
        { value: 'MAD', label: 'Madrid', name: 'Madrid' },
        { value: 'ROM', label: 'Rome', name: 'Rome' },
        { value: 'AMS', label: 'Amsterdam', name: 'Amsterdam' },
        { value: 'YYZ', label: 'Toronto', name: 'Toronto' },
        { value: 'MEX', label: 'Mexico City', name: 'Mexico City' },
        { value: 'GRU', label: 'São Paulo', name: 'São Paulo' },
        { value: 'JNB', label: 'Johannesburg', name: 'Johannesburg' },
        { value: 'DEL', label: 'Delhi', name: 'Delhi' },
        { value: 'BKK', label: 'Bangkok', name: 'Bangkok' },
        { value: 'PEK', label: 'Beijing', name: 'Beijing' }
    ];

    // Convert date strings to Date objects when initializing from existing data
    const processedInitialData = {
        ...initialData,
        // Handle different field naming between storage and form
        departureDate: initialData.departureTime ? new Date(initialData.departureTime) : null,
        arrivalDate: initialData.arrivalTime ? new Date(initialData.arrivalTime) : null
    };

    // Helper function to transform city strings to objects for the form
    const transformCityToObject = (cityName) => {
        if (!cityName) return '';
        
        // If it's already an object, return it as is
        if (typeof cityName === 'object') return cityName;
        
        // Try to find a match in standard cities
        const matchingCity = STANDARD_CITIES.find(city => 
            city.name === cityName || 
            city.label === cityName || 
            city.value === cityName
        );
        
        if (matchingCity) {
            return matchingCity;
        }
        
        // If no match, create a custom city object
        return {
            name: cityName,
            label: cityName,
            value: cityName.toLowerCase().replace(/\s+/g, '-'),
            isCustom: true
        };
    };

    // Transform city strings to objects in initial data
    if (initialData.departureCity && typeof initialData.departureCity === 'string') {
        processedInitialData.departureCity = transformCityToObject(initialData.departureCity);
    }

    if (initialData.arrivalCity && typeof initialData.arrivalCity === 'string') {
        processedInitialData.arrivalCity = transformCityToObject(initialData.arrivalCity);
    }

    // Helper function to transform airline strings to objects for the form
    const transformAirlineToObject = (airlineName) => {
        if (!airlineName) return '';
        
        // If it's already an object, return it as is
        if (typeof airlineName === 'object') return airlineName;
        
        // Check if it has format "CODE - Name"
        let code = '';
        let name = airlineName;
        
        if (airlineName.includes(' - ')) {
            const parts = airlineName.split(' - ');
            code = parts[0].trim();
            name = parts[1].trim();
        }
        
        // Create a custom airline object
        return {
            name: name,
            label: airlineName,  // Keep the original string as label
            code: code,
            value: code || name.toLowerCase().replace(/\s+/g, '-'),
            isCustom: true
        };
    };

    // Transform airline strings to objects in initial data
    if (initialData.airline && typeof initialData.airline === 'string') {
        processedInitialData.airline = transformAirlineToObject(initialData.airline);
        console.log("Transformed airline string to object:", initialData.airline, "→", processedInitialData.airline);
    }

    // Ensure there's at least one passenger when initializing the form
    const initialPassengers = Array.isArray(processedInitialData.passengers) && 
        processedInitialData.passengers.length > 0 ? 
        processedInitialData.passengers : 
        [{
            firstName: '',
            lastName: '',
            seat: '',
            mealPreference: 'none'
        }];

    const [formData, setFormData] = useState({
        // Ensure all fields have default values
        airline: '',
        flightNumber: '',
        departureCity: '',
        departureAirport: '',
        arrivalCity: '',
        arrivalAirport: '',
        departureDate: null,
        arrivalDate: null,
        departureTimezone: '',
        arrivalTimezone: '',
        bookingReference: '',
        bookingAgent: '',  // Add this field with default value
        class: '',
        baggage: '',
        notes: '',
        // Custom city fields
        customDepartureCity: '',
        customArrivalCity: '',
        // Copy over any provided initial data
        ...processedInitialData,
        // Ensure arrays are properly initialized
        passengers: initialPassengers,
        ticketNumbers: processedInitialData.ticketNumbers || ['']
    });
    const [validationState, setValidationState] = useState({ errors: {}, warnings: {} });
    const [touched, setTouched] = useState({});
    const [inference, setInference] = useState({ airline: false });
    const [passengers, setPassengers] = useState([{ id: 1 }]);
    const [ticketNumbers, setTicketNumbers] = useState([""]);
    const [flightTitle, setFlightTitle] = useState('');
    const [airlineOptions, setAirlineOptions] = useState([]);
    const [loadingAirlines, setLoadingAirlines] = useState(false);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({});
    
    // Custom input refs for auto-focus
    const customDepartureCityInputRef = useRef(null);
    const customArrivalCityInputRef = useRef(null);

    // Create a mapping of cities to timezones
    const CITY_TIMEZONES = {
        'NYC': 'America/New_York',
        'LON': 'Europe/London',
        'TOK': 'Asia/Tokyo',
        'LAX': 'America/Los_Angeles',
        'PAR': 'Europe/Paris',
        'SYD': 'Australia/Sydney',
        'HKG': 'Asia/Hong_Kong',
        'SIN': 'Asia/Singapore',
        'DXB': 'Asia/Dubai',
        'FRA': 'Europe/Berlin',
        'MAD': 'Europe/Madrid',
        'ROM': 'Europe/Rome',
        'AMS': 'Europe/Amsterdam',
        'YYZ': 'America/Toronto',
        'MEX': 'America/Mexico_City',
        'GRU': 'America/Sao_Paulo',
        'JNB': 'Africa/Johannesburg',
        'DEL': 'Asia/Kolkata',
        'BKK': 'Asia/Bangkok',
        'PEK': 'Asia/Shanghai'
    };

    const CITY_AIRPORTS = {
        'NYC': [
            { code: 'JFK', name: 'John F. Kennedy International' },
            { code: 'LGA', name: 'LaGuardia' },
            { code: 'EWR', name: 'Newark Liberty International' }
        ],
        'LON': [
            { code: 'LHR', name: 'Heathrow' },
            { code: 'LGW', name: 'Gatwick' },
            { code: 'STN', name: 'Stansted' },
            { code: 'LCY', name: 'London City' },
            { code: 'LTN', name: 'Luton' }
        ],
        'TOK': [
            { code: 'NRT', name: 'Narita International' },
            { code: 'HND', name: 'Haneda International' }
        ],
        'LAX': [
            { code: 'LAX', name: 'Los Angeles International' },
            { code: 'BUR', name: 'Hollywood Burbank' },
            { code: 'LGB', name: 'Long Beach' },
            { code: 'SNA', name: 'John Wayne' }
        ],
        'PAR': [
            { code: 'CDG', name: 'Charles de Gaulle' },
            { code: 'ORY', name: 'Orly' },
            { code: 'BVA', name: 'Beauvais' }
        ],
        'SYD': [
            { code: 'SYD', name: 'Kingsford Smith' },
            { code: 'BWU', name: 'Sydney Bankstown' }
        ],
        'HKG': [
            { code: 'HKG', name: 'Hong Kong International' }
        ],
        'SIN': [
            { code: 'SIN', name: 'Changi' }
        ],
        'DXB': [
            { code: 'DXB', name: 'Dubai International' },
            { code: 'DWC', name: 'Al Maktoum International' }
        ],
        'FRA': [
            { code: 'FRA', name: 'Frankfurt am Main' }
        ],
        'MAD': [
            { code: 'MAD', name: 'Adolfo Suárez Madrid–Barajas' }
        ],
        'ROM': [
            { code: 'FCO', name: 'Leonardo da Vinci–Fiumicino' },
            { code: 'CIA', name: 'Rome Ciampino' }
        ],
        'AMS': [
            { code: 'AMS', name: 'Amsterdam Schiphol' }
        ],
        'YYZ': [
            { code: 'YYZ', name: 'Toronto Pearson' },
            { code: 'YTZ', name: 'Billy Bishop Toronto City' }
        ],
        'MEX': [
            { code: 'MEX', name: 'Benito Juárez International' }
        ],
        'GRU': [
            { code: 'GRU', name: 'São Paulo–Guarulhos International' },
            { code: 'CGH', name: 'Congonhas' }
        ],
        'JNB': [
            { code: 'JNB', name: 'O.R. Tambo International' }
        ],
        'DEL': [
            { code: 'DEL', name: 'Indira Gandhi International' }
        ],
        'BKK': [
            { code: 'BKK', name: 'Suvarnabhumi' },
            { code: 'DMK', name: 'Don Mueang International' }
        ],
        'PEK': [
            { code: 'PEK', name: 'Beijing Capital International' },
            { code: 'PKX', name: 'Beijing Daxing International' }
        ]
    };

    // Update timezone when city or airport is selected
    useEffect(() => {
        // Handle departure city timezone
        if (formData.departureCity && CITY_TIMEZONES[formData.departureCity] && !formData.departureTimezone) {
            setFormData(prev => ({
                ...prev,
                departureTimezone: CITY_TIMEZONES[formData.departureCity]
            }));
        }
        
        // Handle arrival city timezone
        if (formData.arrivalCity && CITY_TIMEZONES[formData.arrivalCity] && !formData.arrivalTimezone) {
            setFormData(prev => ({
                ...prev,
                arrivalTimezone: CITY_TIMEZONES[formData.arrivalCity]
            }));
        }
    }, [formData.departureCity, formData.arrivalCity, formData.departureTimezone, formData.arrivalTimezone]);

    useEffect(() => {
        console.log('Form rendered with data:', formData);
        console.log('Validation state:', validationState);
        console.log('Touched fields:', touched);
        console.log('Inference state:', inference);
    }, []);

    useEffect(() => {
        // Only run inference if we haven't already inferred this field
        const inferred = inferMissingFields(formData);
        const hasNewInferences = inferred._inferred && 
            Object.keys(inferred._inferred).some(
                key => !inference[key]
            );
        
        if (hasNewInferences) {
            setInference(prev => ({
                ...prev, 
                ...inferred._inferred
            }));
            
            // Careful with this - only update if there are actual changes
            const hasChanges = Object.keys(inferred).some(
                key => key !== '_inferred' && 
                JSON.stringify(inferred[key]) !== JSON.stringify(formData[key])
            );
            
            if (hasChanges) {
                setFormData(prev => {
                    // Remove the _inferred property before updating state
                    const { _inferred, ...updatedData } = inferred;
                    return { ...prev, ...updatedData };
                });
            }
        }

        // Run validation without triggering state updates if nothing changed
        const validation = validateFlightData(formData);
        if (JSON.stringify(validation) !== JSON.stringify(validationState)) {
            setValidationState(validation);
        }
    }, [formData, inference]);

    // Load airline options - simplified version to avoid dependency loops
    const loadAirlineOptions = () => {
        try {
            // Get airlines list 
            const airlines = flightDataService.getAirlinesList(currentUser?.uid, tripId);
            setAirlineOptions(airlines);
            
            // If we have an airline in formData (from editing), check if it's a custom one
            if (formData.airline) {
                // Don't replace custom airlines
                if (formData.airline.isCustom) {
                    return;
                }
                
                // Try to find a matching standard airline
                const airlineValue = typeof formData.airline === 'object' ? 
                    formData.airline.code || formData.airline.name : formData.airline;
                
                const matchingOption = findMatchingAirline(airlineValue, airlines);
                if (matchingOption && JSON.stringify(matchingOption) !== JSON.stringify(formData.airline)) {
                    setFormData(prev => ({...prev, airline: matchingOption}));
                }
            }
        } catch (error) {
            console.error('Error loading airline options:', error);
        }
    };

    // Helper function to find a matching airline in the options
    const findMatchingAirline = (airlineValue, options) => {
        if (!airlineValue || !options || !options.length) return null;
        
        // Try matching by code first (most reliable)
        if (typeof airlineValue === 'string') {
            return options.find(option => option.code === airlineValue);
        }
        
        // Try matching by value or code
        return options.find(option => 
            (option.value === airlineValue.value) || 
            (option.code === airlineValue.code)
        );
    };
    
    // Consolidated effect for airline handling
    useEffect(() => {
        // Step 1: Load airline options
        loadAirlineOptions();
        
        // Step 2: Only attempt to set airline value after options are loaded
        if (airlineOptions.length > 0 && initialData.airline) {
            const matchingAirline = findMatchingAirline(initialData.airline, airlineOptions);
            
            if (matchingAirline && (!formData.airline || formData.airline.code !== matchingAirline.code)) {
                setFormData(prev => ({
                    ...prev,
                    airline: matchingAirline
                }));
            }
        }
    }, [tripId, currentUser?.uid]); // Only dependencies that should trigger a reload

    // Helper to get city display name from code
    const getCityName = (cityData) => {
        // If it's a string, check if it's a code that needs to be mapped
        if (typeof cityData === 'string') {
            // Return the user-friendly city name from the code
            switch(cityData) {
                case 'NYC': return 'New York';
                case 'LON': return 'London';
                case 'TOK': return 'Tokyo';
                case 'LAX': return 'Los Angeles';
                case 'PAR': return 'Paris';
                case 'SYD': return 'Sydney';
                case 'HKG': return 'Hong Kong';
                case 'SIN': return 'Singapore';
                case 'DXB': return 'Dubai';
                case 'FRA': return 'Frankfurt';
                case 'MAD': return 'Madrid';
                case 'ROM': return 'Rome';
                case 'AMS': return 'Amsterdam';
                case 'YYZ': return 'Toronto';
                case 'MEX': return 'Mexico City';
                case 'GRU': return 'São Paulo';
                case 'JNB': return 'Johannesburg';
                case 'DEL': return 'Delhi';
                case 'BKK': return 'Bangkok';
                case 'PEK': return 'Beijing';
                default: return cityData; // Return the string itself if no match
            }
        }
        
        // If it's an object, extract the name, label, or value
        if (typeof cityData === 'object' && cityData !== null) {
            console.log('Processing city object:', cityData);
            
            // If it has a value property, it might be a code that needs to be mapped
            if (cityData.value && typeof cityData.value === 'string') {
                const cityCode = cityData.value;
                if (['NYC', 'LON', 'TOK', 'LAX', 'PAR', 'SYD', 'HKG', 'SIN', 'DXB', 'FRA', 
                      'MAD', 'ROM', 'AMS', 'YYZ', 'MEX', 'GRU', 'JNB', 'DEL', 'BKK', 'PEK'].includes(cityCode)) {
                    return getCityName(cityCode); // Recursively call with the code
                }
            }
            
            // Otherwise use the name or label directly
            return cityData.name || cityData.label || cityData.value || 
                   (cityData.toString && cityData.toString() !== '[object Object]' ? 
                    cityData.toString() : 'Unknown City');
        }
        
        // Fallback for null or undefined
        return 'Unknown City';
    };

    // Add a helper function to format the date
    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Update the title construction useEffect
    useEffect(() => {
        // Update display title when form data changes
        let newTitle = '';
        
        // Part 1: Airline and flight number
        if (formData.airline) {
            // Get clean airline name - no code prefix
            let airlineName = '';
            let airlineCode = '';
            
            // Handle custom airline name
            if (typeof formData.airline === 'object' && formData.airline.isCustom) {
                // Use the custom name as entered by the user
                airlineName = formData.airline.name || formData.airline.label || formData.airline.value;
            }
            else if (typeof formData.airline === 'object') {
                // If we have an airline object with a code, use that for the code part
                if (formData.airline.code) {
                    airlineCode = formData.airline.code;
                }
                
                // If we have an airline object with a name property
                if (formData.airline.name) {
                    // Check if name has "CODE - Name" format
                    if (formData.airline.name.includes(' - ')) {
                        airlineName = formData.airline.name.split(' - ')[1].trim();
                    } else {
                        airlineName = formData.airline.name;
                    }
                }
                // If we have a code, look up the name from our airlines list
                else if (formData.airline.code) {
                    const masterAirline = airlineOptions.find(a => a.code === formData.airline.code);
                    if (masterAirline) {
                        airlineName = masterAirline.name;
                    }
                }
                // Try the label if nothing else
                else if (formData.airline.label && formData.airline.label.includes(' - ')) {
                    airlineName = formData.airline.label.split(' - ')[1].trim();
                }
            } else if (typeof formData.airline === 'string') {
                // If it's just a string, check if it has "CODE - Name" format
                if (formData.airline.includes(' - ')) {
                    const parts = formData.airline.split(' - ');
                    airlineCode = parts[0].trim();
                    airlineName = parts[1].trim();
                } else {
                    airlineName = formData.airline;
                }
            }
            
            // Use only the airline name in the title
            newTitle = airlineName;
            
            // Add flight number if available, but don't duplicate airline code
            if (formData.flightNumber) {
                newTitle += ' ' + formData.flightNumber;
            }
        } else if (formData.flightNumber) {
            newTitle = formData.flightNumber;
        }
        
        // Part 2: Route - Format as "City to City"
        if (formData.departureCity || formData.arrivalCity) {
            if (newTitle) newTitle += ', ';
            const departure = formData.departureCity ? getCityName(formData.departureCity) : 'Departure';
            const arrival = formData.arrivalCity ? getCityName(formData.arrivalCity) : 'Arrival';
            newTitle += `${departure} to ${arrival}`;
        }
        
        // Part 3: Add departure date in format "15 Jun 2025"
        if (formData.departureDate) {
            const departureDate = formData.departureDate;
            
            if (departureDate && !isNaN(departureDate.getTime())) {
                if (newTitle) newTitle += ' (';
                
                // Format date as "15 Jun 2025"
                const day = departureDate.getDate();
                const month = departureDate.toLocaleString('en-US', { month: 'short' });
                const year = departureDate.getFullYear();
                
                newTitle += `${day} ${month} ${year}`;
                
                if (newTitle.endsWith('(')) newTitle += ')';
                else if (!newTitle.endsWith(')')) newTitle += ')';
            }
        }
        
        setFlightTitle(newTitle || 'New Flight');
    }, [formData.airline, formData.flightNumber, formData.departureCity, formData.arrivalCity, formData.departureDate, airlineOptions]);

    // Enhanced handleInputChange function to set timezone when city is selected
    const handleInputChange = (fieldName, value) => {
        setTouched(prev => ({ ...prev, [fieldName]: true }));
        
        console.log(`Handling input change for ${fieldName}:`, value);
        
        // Special handling for city changes - reset airport when city changes
        if (fieldName === 'departureCity') {
            // When using EnhancedSelect, value is an object
            if (value && typeof value === 'object') {
                const cityCode = value.value; // e.g. 'NYC'
                const updates = {
                    [fieldName]: value,
                    departureAirport: ''
                };
                
                // Set timezone if available
                if (CITY_TIMEZONES[cityCode]) {
                    updates.departureTimezone = CITY_TIMEZONES[cityCode];
                }
                
                setFormData(prev => ({
                    ...prev,
                    ...updates
                }));
            } else {
                // Fallback for string values
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: value,
                    departureAirport: ''
                }));
            }
        } else if (fieldName === 'arrivalCity') {
            // When using EnhancedSelect, value is an object
            if (value && typeof value === 'object') {
                const cityCode = value.value; // e.g. 'NYC'
                const updates = {
                    [fieldName]: value,
                    arrivalAirport: ''
                };
                
                // Set timezone if available
                if (CITY_TIMEZONES[cityCode]) {
                    updates.arrivalTimezone = CITY_TIMEZONES[cityCode];
                }
                
                setFormData(prev => ({
                    ...prev,
                    ...updates
                }));
            } else {
                // Fallback for string values
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: value,
                    arrivalAirport: ''
                }));
            }
        } else if (fieldName === 'departureAirport' || fieldName === 'arrivalAirport') {
            // Only update the airport field, not touching the city
            setFormData(prev => ({
                ...prev,
                [fieldName]: value
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: value
            }));
        }

        // If there was an error for this field, clear it
        if (error) {
            setError('');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        try {
            console.log("Attempting to submit flight form with data:", formData);
            
            // Check if we have enough info to create a title
            const hasTitleInfo = formData.airline || 
                formData.flightNumber || 
                formData.departureCity || 
                formData.arrivalCity || 
                formData.departureAirport || 
                formData.arrivalAirport;
                
            if (!hasTitleInfo) {
                setError('Please provide some flight information before saving.');
                return;
            }
            
            // Create a deep copy to avoid modifying state
            const normalizedData = JSON.parse(JSON.stringify(formData));
            
            // Process city data from EnhancedSelect
            if (normalizedData.departureCity && typeof normalizedData.departureCity === 'object') {
                // For standard cities, use the name property
                if (normalizedData.departureCity.value) {
                    console.log(`Processing departureCity: ${JSON.stringify(normalizedData.departureCity)}`);
                    const cityCode = normalizedData.departureCity.value;
                    
                    // If it's a predefined city, use the proper name
                    if (CITY_TIMEZONES[cityCode]) {
                        normalizedData.departureCity = normalizedData.departureCity.name || 
                                                      normalizedData.departureCity.label || 
                                                      getCityName(cityCode);
                    } else {
                        // It's a custom city, use the name as entered
                        normalizedData.departureCity = normalizedData.departureCity.name || 
                                                      normalizedData.departureCity.label;
                    }
                } else {
                    // Fallback to use whatever is in the object
                    normalizedData.departureCity = normalizedData.departureCity.name || 
                                                  normalizedData.departureCity.label || 
                                                  "";
                }
            }
            
            if (normalizedData.arrivalCity && typeof normalizedData.arrivalCity === 'object') {
                // For standard cities, use the name property
                if (normalizedData.arrivalCity.value) {
                    console.log(`Processing arrivalCity: ${JSON.stringify(normalizedData.arrivalCity)}`);
                    const cityCode = normalizedData.arrivalCity.value;
                    
                    // If it's a predefined city, use the proper name
                    if (CITY_TIMEZONES[cityCode]) {
                        normalizedData.arrivalCity = normalizedData.arrivalCity.name || 
                                                    normalizedData.arrivalCity.label || 
                                                    getCityName(cityCode);
                    } else {
                        // It's a custom city, use the name as entered
                        normalizedData.arrivalCity = normalizedData.arrivalCity.name || 
                                                    normalizedData.arrivalCity.label;
                    }
                } else {
                    // Fallback to use whatever is in the object
                    normalizedData.arrivalCity = normalizedData.arrivalCity.name || 
                                               normalizedData.arrivalCity.label || 
                                               "";
                }
            }
            
            // Fix for Date objects that were stringified during cloning
            // Store departure and arrival dates properly before they get modified
            const departureDateObj = formData.departureDate instanceof Date ? formData.departureDate : null;
            const arrivalDateObj = formData.arrivalDate instanceof Date ? formData.arrivalDate : null;
            
            // Normalize airline data
            if (normalizedData.airline && typeof normalizedData.airline === 'object') {
                // If it's a custom airline, use the name
                if (normalizedData.airline.isCustom) {
                    normalizedData.airline = normalizedData.airline.name || '';
                } else {
                    // For standard airlines, use the label (which includes code if available)
                    normalizedData.airline = normalizedData.airline.label || 
                                             normalizedData.airline.name ||
                                             normalizedData.airline.value || 
                                             '';
                }
                
                // Log how airline was normalized
                console.log("Normalized airline:", formData.airline, "→", normalizedData.airline);
            }
            
            // Normalize airport data
            ['departureAirport', 'arrivalAirport'].forEach(field => {
                if (normalizedData[field] && typeof normalizedData[field] === 'object') {
                    normalizedData[field] = normalizedData[field].value || 
                                            normalizedData[field].label || 
                                            normalizedData[field].code || 
                                            '';
                }
            });
            
            // Add departureTime and arrivalTime using the preserved Date objects
            if (departureDateObj) {
                normalizedData.departureTime = departureDateObj.toISOString();
            }
            
            if (arrivalDateObj) {
                normalizedData.arrivalTime = arrivalDateObj.toISOString();
            }
            
            // Add ticketNumbers to the submission
            normalizedData.ticketNumbers = ticketNumbers.filter(t => t && t.trim() !== '');
            
            // Important: For Firestore, replace any undefined values with null or empty string
            // First, define all the fields that should exist in the details object
            const allFlightFields = [
                'airline', 'flightNumber', 'departureCity', 'departureAirport', 
                'arrivalCity', 'arrivalAirport', 'departureTime', 'arrivalTime',
                'departureDate', 'arrivalDate', 'departureTimezone', 'arrivalTimezone',
                'bookingReference', 'bookingAgent', 'bookingLink', 'class', 'classOfService',
                'baggage', 'baggageAllowance', 'notes'
            ];
            
            // Make sure all fields exist (with empty value if needed)
            allFlightFields.forEach(field => {
                if (normalizedData[field] === undefined) {
                    normalizedData[field] = null;
                }
            });
            
            // Clean up any null/empty passengers or ticket arrays
            if (normalizedData.passengers && normalizedData.passengers.length === 0) {
                normalizedData.passengers = null;
            }
            
            if (normalizedData.ticketNumbers && normalizedData.ticketNumbers.length === 0) {
                normalizedData.ticketNumbers = null;
            }
            
            // Map the class field to classOfService for compatibility with AddTravel.js
            normalizedData.classOfService = normalizedData.class;
            normalizedData.baggageAllowance = normalizedData.baggage;
            
            // Add the flight title as the summary field
            normalizedData.summary = flightTitle;
            
            console.log("Submitting normalized data:", normalizedData);
            
            // Clear error before submission
            setError('');
            
            // Submit the data as expected by AddTravel.js
            onSubmit({
                details: normalizedData,
                summary: flightTitle
            });
        } catch (error) {
            console.error('Error submitting flight form:', error);
            setError('An error occurred while saving the flight information. Please try again.');
        }
    };

    const getFieldError = (fieldName) => {
        return touched[fieldName] ? validationState.errors[fieldName] : null;
    };

    const renderFieldError = (fieldName) => {
        const error = getFieldError(fieldName);
        return error && <div className="error-message">{error}</div>;
    };

    // Check if a passenger has any data entered
    const hasPassengerData = (passenger) => {
        return passenger.firstName || 
               passenger.lastName || 
               passenger.seat || 
               (passenger.mealPreference && passenger.mealPreference !== 'none');
    };

    // Update the addPassenger function to check if the last passenger has data
    const addPassenger = () => {
        // Check if the last passenger has any data entered
        const lastPassengerIndex = formData.passengers.length - 1;
        const lastPassenger = formData.passengers[lastPassengerIndex];
        
        // Only allow adding if the last passenger has some data
        if (hasPassengerData(lastPassenger)) {
            setFormData(prev => ({
                ...prev,
                passengers: [...prev.passengers, {
                    firstName: '',
                    lastName: '',
                    seat: '',
                    mealPreference: 'none'
                }]
            }));
        } else {
            // Optionally show a message that the current passenger needs data
            alert("Please enter information for the current passenger before adding another.");
        }
    };

    // Remove a passenger by index
    const removePassenger = (index) => {
        // If we're trying to remove the last passenger, just clear their fields
        if (formData.passengers.length === 1) {
            clearFirstPassenger();
            return;
        }
        
        // Otherwise, remove the passenger
        setFormData(prev => ({
            ...prev,
            passengers: prev.passengers.filter((_, i) => i !== index)
        }));
    };

    // Clear the first passenger's fields
    const clearFirstPassenger = () => {
        setFormData(prev => ({
            ...prev,
            passengers: [
                {
                    firstName: '',
                    lastName: '',
                    seat: '',
                    mealPreference: 'none'
                },
                ...prev.passengers.slice(1) // Keep all other passengers
            ]
        }));
    };

    const addTicketNumber = () => {
        setTicketNumbers(prev => [...prev, ""]);
    };

    const updateTicketNumber = (index, value) => {
        setTicketNumbers(prev => {
            const updated = [...prev];
            updated[index] = value;
            return updated;
        });
    };

    const removeTicketNumber = (index) => {
        setTicketNumbers(prev => prev.filter((_, i) => i !== index));
    };

    const renderField = (fieldName, label, component) => {
        const hasTitleInfo = formData.airline || 
            formData.flightNumber || 
            formData.departureCity || 
            formData.arrivalCity || 
            formData.departureAirport || 
            formData.arrivalAirport;

        const isRequired = !hasTitleInfo && FLIGHT_FIELDS[fieldName]?.required;

        return (
            <div className="form-group">
                <label htmlFor={fieldName}>
                    {label}
                    {isRequired && <span className="required">*</span>}
                </label>
                {component}
                {renderFieldError(fieldName)}
            </div>
        );
    };

    // Create city options for EnhancedSelect
    const getCityOptions = () => {
        return STANDARD_CITIES;
    };
    
    // Create city options
    const cityOptions = getCityOptions();

    // Log form state changes
    useEffect(() => {
        console.log("City/Airport state updated:", {
            departureCity: formData.departureCity,
            departureAirport: formData.departureAirport,
            arrivalCity: formData.arrivalCity,
            arrivalAirport: formData.arrivalAirport
        });
    }, [formData.departureCity, formData.departureAirport, formData.arrivalCity, formData.arrivalAirport]);

    // Log when component mounts with initial data
    useEffect(() => {
        console.log("Component mounted with initialData:", initialData);
        console.log("Processed initial data:", processedInitialData);
        console.log("Initial form data state:", formData);
    }, []);

    return (
        <div className="flight-form">
            <div className="flight-title">
                <h1>Flight Details</h1>
                {flightTitle ? (
                    <div className="flight-title-display">{flightTitle}</div>
                ) : (
                    <div className="flight-title-placeholder">
                        A title for this flight will be displayed here as you enter values in the fields below.
                    </div>
                )}
            </div>

            <div className="auto-extract">
                <button type="button" onClick={() => window.location.href='extract.html'}>
                    Auto Extract Data
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Flight Identification Section */}
                <div className="form-section">
                    <h2 className="section-title">Flight Identification</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="airline">Airline:</label>
                            <EnhancedSelect
                                options={airlineOptions}
                                onChange={(selected) => {
                                    // Call the onSelect handler to track selection
                                    const processedOption = flightFields?.airline?.onSelect ? 
                                        flightFields.airline.onSelect(selected) : 
                                        selected;
                                    handleInputChange('airline', processedOption);
                                }}
                                value={formData.airline}
                                placeholder={loadingAirlines ? "Loading airlines..." : "Select or type any airline name"}
                                isSearchable={true}
                                isLoading={loadingAirlines}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any airline name and press Enter
                            </small>
                            {formData.airline && formData.airline.isCustom && (
                                <small className="custom-input-note">Using custom airline name: "{formData.airline.name}"</small>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="flightNumber">Flight Number:</label>
                            <input
                                type="text"
                                id="flightNumber"
                                value={formData.flightNumber}
                                onChange={(e) => handleInputChange('flightNumber', e.target.value)}
                                placeholder="e.g. AA123"
                            />
                        </div>
                    </div>
                </div>

                {/* Departure Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Departure Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="departureCity">
                                Departure City
                            </label>
                            <EnhancedSelect
                                options={cityOptions}
                                onChange={(selected) => handleInputChange('departureCity', selected)}
                                value={formData.departureCity}
                                placeholder="Select or type any city name"
                                isSearchable={true}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any city name and press Enter
                            </small>
                            {formData.departureCity && formData.departureCity.isCustom && (
                                <small className="custom-input-note">Using custom city: "{formData.departureCity.name}"</small>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="departureAirport">Departure Airport</label>
                            <div className="custom-select-wrapper">
                                {formData.departureCity && formData.departureCity.value && CITY_AIRPORTS[formData.departureCity.value] ? (
                                    <select
                                        id="departureAirport"
                                        value={formData.departureAirport}
                                        onChange={(e) => handleInputChange('departureAirport', e.target.value)}
                                    >
                                        <option value="">Select Airport</option>
                                        {CITY_AIRPORTS[formData.departureCity.value]?.map(airport => (
                                            <option key={airport.code} value={airport.code}>
                                                {airport.code} - {airport.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        id="departureAirport"
                                        value={formData.departureAirport}
                                        onChange={(e) => handleInputChange('departureAirport', e.target.value)}
                                        placeholder="Enter airport code or name"
                                    />
                                )}
                                {(!formData.departureCity || !formData.departureCity.value || !CITY_AIRPORTS[formData.departureCity.value]) && formData.departureCity && formData.departureCity.isCustom && (
                                    <small className="field-hint">
                                        Enter airport for <strong>{formData.departureCity.name}</strong>
                                    </small>
                                )}
                                {(!formData.departureCity || !formData.departureCity.value || !CITY_AIRPORTS[formData.departureCity.value]) && (!formData.departureCity || !formData.departureCity.isCustom) && (
                                    <small className="field-hint">
                                        Enter any airport code or name
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="departureDate">Departure Date & Time</label>
                            <DatePicker
                                selected={formData.departureDate}
                                onChange={(date) => handleInputChange('departureDate', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="departureTimezone">Departure Timezone</label>
                        <TimezoneSelect
                            value={formData.departureTimezone}
                            onChange={(tz) => handleInputChange('departureTimezone', tz.value)}
                            className="form-control"
                        />
                    </div>
                </div>

                {/* Arrival Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Arrival Information</h2>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="arrivalCity">
                                Arrival City
                            </label>
                            <EnhancedSelect
                                options={cityOptions}
                                onChange={(selected) => handleInputChange('arrivalCity', selected)}
                                value={formData.arrivalCity}
                                placeholder="Select or type any city name"
                                isSearchable={true}
                                allowCustomValues={true}
                            />
                            <small className="field-hint">
                                Select from the list or type any city name and press Enter
                            </small>
                            {formData.arrivalCity && formData.arrivalCity.isCustom && (
                                <small className="custom-input-note">Using custom city: "{formData.arrivalCity.name}"</small>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="arrivalAirport">Arrival Airport</label>
                            <div className="custom-select-wrapper">
                                {formData.arrivalCity && formData.arrivalCity.value && CITY_AIRPORTS[formData.arrivalCity.value] ? (
                                    <select
                                        id="arrivalAirport"
                                        value={formData.arrivalAirport}
                                        onChange={(e) => handleInputChange('arrivalAirport', e.target.value)}
                                    >
                                        <option value="">Select Airport</option>
                                        {CITY_AIRPORTS[formData.arrivalCity.value]?.map(airport => (
                                            <option key={airport.code} value={airport.code}>
                                                {airport.code} - {airport.name}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type="text"
                                        id="arrivalAirport"
                                        value={formData.arrivalAirport}
                                        onChange={(e) => handleInputChange('arrivalAirport', e.target.value)}
                                        placeholder="Enter airport code or name"
                                    />
                                )}
                                {(!formData.arrivalCity || !formData.arrivalCity.value || !CITY_AIRPORTS[formData.arrivalCity.value]) && formData.arrivalCity && formData.arrivalCity.isCustom && (
                                    <small className="field-hint">
                                        Enter airport for <strong>{formData.arrivalCity.name}</strong>
                                    </small>
                                )}
                                {(!formData.arrivalCity || !formData.arrivalCity.value || !CITY_AIRPORTS[formData.arrivalCity.value]) && (!formData.arrivalCity || !formData.arrivalCity.isCustom) && (
                                    <small className="field-hint">
                                        Enter any airport code or name
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="arrivalDate">Arrival Date & Time</label>
                            <DatePicker
                                selected={formData.arrivalDate}
                                onChange={(date) => handleInputChange('arrivalDate', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                className="form-control"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="arrivalTimezone">Arrival Timezone</label>
                        <TimezoneSelect
                            value={formData.arrivalTimezone}
                            onChange={(tz) => handleInputChange('arrivalTimezone', tz.value)}
                            className="form-control"
                        />
                    </div>
                </div>

                {/* Booking Details Section */}
                <div className="form-section">
                    <h2 className="section-title">Booking Details</h2>
                    <div className="form-group">
                        <label htmlFor="bookingAgent">Booking Agent:</label>
                        <input
                            type="text"
                            id="bookingAgent"
                            value={formData.bookingAgent}
                            onChange={(e) => handleInputChange('bookingAgent', e.target.value)}
                            placeholder="Enter booking agent"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="bookingReference">Booking Reference (PNR):</label>
                        <input
                            type="text"
                            id="bookingReference"
                            value={formData.bookingReference}
                            onChange={(e) => handleInputChange('bookingReference', e.target.value)}
                            placeholder="Enter booking reference"
                        />
                    </div>

                    <div className="form-group">
                        <label>Ticket Number(s):</label>
                        {ticketNumbers.map((ticket, index) => (
                            <div key={index} className="ticket-input-group">
                                <input
                                    type="text"
                                    value={ticket}
                                    onChange={(e) => updateTicketNumber(index, e.target.value)}
                                    placeholder="Enter ticket number"
                                />
                                {index > 0 && (
                                    <button
                                        type="button"
                                        className="remove-button"
                                        onClick={() => removeTicketNumber(index)}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            type="button"
                            className="add-button"
                            onClick={addTicketNumber}
                        >
                            + Add Another Ticket Number
                        </button>
                    </div>
                </div>

                {/* Passenger Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Passenger Information</h2>
                    {formData.passengers.map((passenger, index) => (
                        <div key={index} className="passenger-container">
                            <h3 className="passenger-title">Passenger {index + 1}</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>First Name:</label>
                                    <input
                                        type="text"
                                        value={passenger.firstName || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.firstName`, e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Last Name:</label>
                                    <input
                                        type="text"
                                        value={passenger.lastName || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.lastName`, e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Seat:</label>
                                    <input
                                        type="text"
                                        value={passenger.seat || ''}
                                        onChange={(e) => handleInputChange(`passengers.${index}.seat`, e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Meal Preference:</label>
                                    <select
                                        value={passenger.mealPreference || 'none'}
                                        onChange={(e) => handleInputChange(`passengers.${index}.mealPreference`, e.target.value)}
                                    >
                                        <option value="none">None</option>
                                        <option value="vegetarian">Vegetarian</option>
                                        <option value="vegan">Vegan</option>
                                        <option value="gluten-free">Gluten-Free</option>
                                        <option value="kosher">Kosher</option>
                                        <option value="halal">Halal</option>
                                    </select>
                                </div>
                            </div>
                            {/* Buttons for this passenger */}
                            <div className="passenger-buttons">
                                {formData.passengers.length === 1 && hasPassengerData(passenger) ? (
                                    <button
                                        type="button"
                                        className="clear-button"
                                        onClick={clearFirstPassenger}
                                    >
                                        Clear Fields
                                    </button>
                                ) : formData.passengers.length > 1 ? (
                                    <button
                                        type="button"
                                        className="remove-button"
                                        onClick={() => removePassenger(index)}
                                    >
                                        Remove Passenger
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ))}
                    {/* Add passenger button */}
                    <div className="add-button-container">
                        <button
                            type="button"
                            className="add-button"
                            onClick={addPassenger}
                        >
                            + Add Another Passenger
                        </button>
                    </div>
                </div>

                {/* Additional Information Section */}
                <div className="form-section">
                    <h2 className="section-title">Additional Information (Optional)</h2>
                    <div className="form-group">
                        <label htmlFor="classOfService">Class of Service:</label>
                        <select
                            id="classOfService"
                            value={formData.classOfService}
                            onChange={(e) => handleInputChange('classOfService', e.target.value)}
                        >
                            <option value="economy">Economy</option>
                            <option value="premium_economy">Premium Economy</option>
                            <option value="business">Business</option>
                            <option value="first">First</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label htmlFor="baggageAllowance">Baggage Allowance:</label>
                        <input
                            type="text"
                            id="baggageAllowance"
                            value={formData.baggageAllowance}
                            onChange={(e) => handleInputChange('baggageAllowance', e.target.value)}
                            placeholder="Enter baggage allowance"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="notes">Notes/Comments:</label>
                        <textarea
                            id="notes"
                            value={formData.notes}
                            onChange={(e) => handleInputChange('notes', e.target.value)}
                            rows="4"
                        />
                    </div>
                </div>

                <div className="form-actions">
                    <button 
                        type="submit" 
                        className="save-button"
                    >
                        Save Flight
                    </button>
                    <button 
                        type="button" 
                        className="cancel-button" 
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddFlightForm;